import { useState } from "react";
import { Zap, AlertTriangle, DollarSign, ArrowRight, Shield, TrendingUp, Building2, Briefcase, ShieldAlert, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import AIEventGuidance from "@/components/ai/AIEventGuidance";
import type { ActiveLifeEvent, EventChoice } from "@/hooks/useLifeEvents";
import type { AnyValue } from "@/lib/rowTypes";

interface Props {
  event: ActiveLifeEvent;
  onResolve: (choiceIndex: number) => Promise<EventChoice | null>;
  onDismiss: () => void;
}

const CATEGORY_CONFIG: Record<string, { icon: AnyValue; color: string; label: string }> = {
  personal_finance: { icon: DollarSign, color: "text-primary", label: "Personal Finance" },
  housing: { icon: Building2, color: "text-amber-500", label: "Housing" },
  career: { icon: Briefcase, color: "text-blue-500", label: "Career" },
  investing: { icon: TrendingUp, color: "text-emerald-500", label: "Investing" },
  entrepreneurship: { icon: Building2, color: "text-violet-500", label: "Business" },
  scam_protection: { icon: ShieldAlert, color: "text-destructive", label: "Scam Alert" },
  behavior: { icon: Heart, color: "text-pink-500", label: "Behavior" },
};

const QUALITY_STYLES: Record<string, string> = {
  excellent: "border-emerald-500/40 bg-emerald-500/5",
  good: "border-primary/30 bg-primary/5",
  fair: "border-amber-500/30 bg-amber-500/5",
  poor: "border-destructive/30 bg-destructive/5",
};

const LifeEventCard = ({ event, onResolve, onDismiss }: Props) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [resolvedChoice, setResolvedChoice] = useState<EventChoice | null>(null);
  const [resolving, setResolving] = useState(false);

  const { template, resolvedDescription } = event;
  const config = CATEGORY_CONFIG[template.event_category] || CATEGORY_CONFIG.personal_finance;
  const Icon = config.icon;

  const handleChoice = async (index: number) => {
    if (selectedIndex !== null) return;
    setResolving(true);
    setSelectedIndex(index);
    const choice = await onResolve(index);
    setResolvedChoice(choice);
    setResolving(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" />

      <div className="relative w-full max-w-md rounded-2xl border-2 border-border bg-card p-6 space-y-5 shadow-2xl animate-fade-in overflow-y-auto max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-muted/50`}>
            <Icon className={`w-6 h-6 ${config.color}`} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className={`text-[10px] font-semibold uppercase tracking-widest ${config.color}`}>
                {config.label}
              </span>
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground capitalize">
                {template.event_difficulty}
              </span>
            </div>
            <h2 className="font-heading font-bold text-lg text-foreground">{template.event_name}</h2>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-muted-foreground leading-relaxed">{resolvedDescription}</p>

        {/* AI Guidance */}
        {selectedIndex === null && (
          <AIEventGuidance
            eventTitle={template.event_name}
            eventDescription={resolvedDescription}
            eventType="life_event"
          />
        )}

        {/* Choices */}
        {selectedIndex === null && (
          <div className="space-y-2">
            {template.event_choices.map((choice, i) => (
              <button
                key={i}
                onClick={() => handleChoice(i)}
                disabled={resolving}
                className="w-full text-left rounded-xl border border-border px-4 py-3 transition-all duration-200 hover:border-primary/50 hover:bg-primary/5"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-foreground">{choice.label}</span>
                  <div className="flex items-center gap-1 shrink-0">
                    {choice.amount > 0 && (
                      <span className={`text-xs font-semibold ${choice.is_deduction ? "text-destructive" : "text-emerald-500"}`}>
                        {choice.is_deduction ? "-" : "+"}${choice.amount}
                      </span>
                    )}
                    <ArrowRight className="w-3 h-3 text-muted-foreground" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Result */}
        {resolvedChoice && (
          <div className="space-y-4 animate-fade-in">
            {/* Quality badge */}
            <div className={`rounded-xl p-4 border ${QUALITY_STYLES[resolvedChoice.quality] || "border-border bg-muted/5"}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-bold uppercase tracking-wider ${
                  resolvedChoice.quality === "excellent" ? "text-emerald-600" :
                  resolvedChoice.quality === "good" ? "text-primary" :
                  resolvedChoice.quality === "fair" ? "text-amber-600" : "text-destructive"
                }`}>
                  {resolvedChoice.quality === "excellent" ? "Excellent Decision" :
                   resolvedChoice.quality === "good" ? "Good Choice" :
                   resolvedChoice.quality === "fair" ? "Fair Decision" : "Poor Choice"}
                </span>
              </div>
              <p className="text-sm text-foreground leading-relaxed">{resolvedChoice.explanation}</p>
            </div>

            {/* Outcome effects */}
            <div className="grid grid-cols-3 gap-2">
              <OutcomePill label="Stress" delta={resolvedChoice.stress_delta} inverted />
              <OutcomePill label="Resilience" delta={resolvedChoice.resilience_delta} />
              <OutcomePill label="Confidence" delta={resolvedChoice.confidence_delta} />
            </div>

            {/* Followup */}
            {template.event_followup_text && (
              <div className="rounded-lg bg-muted/30 p-3 border border-border">
                <div className="flex items-start gap-2">
                  <Shield className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                  <p className="text-xs text-muted-foreground leading-relaxed">{template.event_followup_text}</p>
                </div>
              </div>
            )}

            <Button onClick={onDismiss} className="w-full">
              Continue Learning →
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

function OutcomePill({ label, delta, inverted = false }: { label: string; delta: number; inverted?: boolean }) {
  // For stress, negative delta = good (less stress)
  const isPositive = inverted ? delta < 0 : delta > 0;
  const isNeutral = delta === 0;

  return (
    <div className={`rounded-lg px-2 py-1.5 text-center border ${
      isNeutral ? "border-border bg-muted/20" :
      isPositive ? "border-emerald-500/30 bg-emerald-500/5" : "border-destructive/30 bg-destructive/5"
    }`}>
      <p className="text-[10px] text-muted-foreground">{label}</p>
      <p className={`text-xs font-bold ${
        isNeutral ? "text-muted-foreground" :
        isPositive ? "text-emerald-600" : "text-destructive"
      }`}>
        {isNeutral ? "—" : `${inverted ? (delta > 0 ? "+" : "") : (delta > 0 ? "+" : "")}${delta}`}
      </p>
    </div>
  );
}

export default LifeEventCard;
